import { FormProvider, useForm, useWatch } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { Button, Stack, Typography } from '@mui/joy';
import { SideDrawer } from '@/components/SideDrawer';
import { FormInput } from '@/components/forms/FormInput';
import { FormCheckbox } from '@/components/forms/FormCheckbox';
import { FormSelect } from '@/components/forms/FormSelect';
import { CategoryPicker } from '@/features/categories/components/CategoryPicker';
import { UnitsSelect } from '@/features/units/components/UnitsSelect';
import { useBulkUpdateIngredientsMutation } from '@/features/ingredients/api';
import {
  BULK_EDIT_ENABLED_NAMES,
  BULK_EDIT_NUMERIC_FIELDS,
  bulkEditChanges,
  bulkEditIngredientsSchema,
  enabledName,
  newBulkIngredientEdit,
} from '@/features/ingredients/schema.js';

const FORM_ID = 'bulk-edit-ingredients';

const COMMON_OPTIONS = [
  { value: true, label: 'Yes' },
  { value: false, label: 'No' },
];

// Lines the input up with the checkbox label rather than the checkbox itself:
// the sm checkbox is 1.25rem wide and sits 0.5rem from its label. The extra
// bottom margin puts more space under an expanded input than above it, so the
// input visibly belongs to the checkbox above rather than the one below.
const FIELD_SX = { ml: 3, mb: 0.5 };

const FIELD_GAP = 0.75;

export function BulkEditIngredientsDrawer({ open, onClose, selectedIds, onApplied }) {
  const [bulkUpdateIngredients, bulkUpdateResult] = useBulkUpdateIngredientsMutation();

  const form = useForm({
    defaultValues: newBulkIngredientEdit(),
    resolver: yupResolver(bulkEditIngredientsSchema),
  });

  const handleClose = () => {
    form.reset(newBulkIngredientEdit());
    onClose();
  };

  // The schema strips every field whose checkbox is off, so what reaches here is
  // already narrowed to the fields being changed.
  async function onSubmit(values) {
    const result = await bulkUpdateIngredients({
      ids: Array.from(selectedIds),
      changes: bulkEditChanges(values),
    });

    if (result.error) {
      console.error(result.error);
      return;
    }

    onApplied?.();
    handleClose();
  }

  return (
    <FormProvider {...form}>
      <SideDrawer
        open={open}
        onClose={handleClose}
        title={`Edit ${selectedIds.size} ingredients`}
        gap={2}
        footer={
          <>
            <Button variant="plain" color="neutral" onClick={handleClose}>
              Cancel
            </Button>
            <ApplyButton form={FORM_ID} count={selectedIds.size} isSaving={bulkUpdateResult.isLoading} />
          </>
        }
      >
        <Typography level="body-sm" textColor="text.tertiary" sx={{ mb: 2 }}>
          Check a field to change it for every selected ingredient. Unchecked fields are left alone.
        </Typography>

        <form id={FORM_ID} onSubmit={form.handleSubmit(onSubmit)}>
          <Stack gap={2.5}>
            <BulkEditField name="subcategory_id" label="Category">
              <CategoryPicker name="subcategory_id" sx={FIELD_SX} />
            </BulkEditField>

            <BulkEditField name="preferred_unit" label="Preferred unit">
              <UnitsSelect name="preferred_unit" sx={FIELD_SX} />
            </BulkEditField>

            <BulkEditField name="common" label="Common ingredient">
              <FormSelect name="common" options={COMMON_OPTIONS} sx={FIELD_SX} />
            </BulkEditField>

            {BULK_EDIT_NUMERIC_FIELDS.map((field) => (
              <BulkEditField key={field.key} name={field.key} label={field.label}>
                <FormInput
                  name={field.key}
                  type="number"
                  inputProps={{ min: 0, step: 0.01 }}
                  sx={FIELD_SX}
                />
              </BulkEditField>
            ))}
          </Stack>
        </form>
      </SideDrawer>
    </FormProvider>
  );
}

// A checkbox deciding whether this field takes part in the update, and the
// field itself once it does.
function BulkEditField({ name, label, children }) {
  const isEnabled = useWatch({ name: enabledName(name) });

  return (
    <Stack gap={FIELD_GAP}>
      <FormCheckbox name={enabledName(name)} label={label} />
      {isEnabled && children}
    </Stack>
  );
}

function ApplyButton({ form, count, isSaving }) {
  const enabled = useWatch({ name: BULK_EDIT_ENABLED_NAMES });
  const hasChanges = enabled.some(Boolean);

  return (
    <Button type="submit" form={form} disabled={!hasChanges} loading={isSaving}>
      Apply to {count} ingredients
    </Button>
  );
}
